import { useEffect, useRef } from 'react';

/**
 * Drives a native `<dialog>` from an `open` prop.
 *
 * Native `showModal()` is used deliberately: it gives focus trapping, Escape,
 * the top layer and `inert` background content for free, all of which are easy
 * to get subtly wrong in a hand-rolled modal. `showModal` is missing in jsdom
 * and in very old browsers, so the hook degrades to the non-modal `open`
 * attribute rather than throwing.
 */
export function useDialogElement(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
    } else if (dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  // The browser fires `cancel` for Escape; translate it into our onClose so the
  // React state and the DOM cannot drift apart.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onCloseRef.current();
    };
    const handleClose = () => {
      onCloseRef.current();
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
    };
  }, []);

  // iOS keeps scrolling the page behind a modal; the top layer does not stop it.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return ref;
}
